import {Field, Formik} from "formik";
import * as Yup from "yup";
import {useEffect, useState} from "react";
import Select from "react-select";
import {useTranslation} from "react-i18next";

let postalZonesPromise;
const loadPostalZones = () => {
  if (!postalZonesPromise) {
    postalZonesPromise = fetch("/data/laposte_hexasmal_sorted.json").then(
      (response) => {
        if (!response.ok) throw new Error("POSTAL_DATA_UNAVAILABLE");
        return response.json();
      },
    );
  }
  return postalZonesPromise;
};

const FilterPrestataire = ({
  setFilter,
  searchTerm,
  setSearchTerm,
  className,
  isNearbyActive,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const formSchema = Yup.object().shape({
    zone: Yup.string().nullable(),
    type: Yup.string().oneOf(["All", "Pro", "Particulier"]),
  });

  useEffect(() => {
    const inputValue = searchTerm.trim();
    if (inputValue.length < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    let isCurrent = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await loadPostalZones();
        const normalizedInput = inputValue.toLowerCase();
        const uniqueOptions = new Map();
        data
          .filter(
            (item) =>
              item.nom_de_la_commune
                .toLowerCase()
                .includes(normalizedInput) ||
              String(item.code_postal).startsWith(inputValue),
          )
          .forEach((item) => {
            if (uniqueOptions.size < 30) {
              uniqueOptions.set(String(item.code_postal), {
                value: String(item.code_postal),
                label: `${item.nom_de_la_commune} (${item.code_postal})`,
              });
            }
          });
        if (isCurrent) setOptions(Array.from(uniqueOptions.values()));
      } catch (error) {
        if (isCurrent) setOptions([]);
      } finally {
        if (isCurrent) setLoading(false);
      }
    }, 200);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={{
        type: "All",
        zone: "",
      }}
      onSubmit={(values, {setSubmitting}) => {
        setFilter((prev) => ({
          ...prev,
          filter: {
            ...prev.filter,
            type: values.type === "All" ? undefined : values.type,
          },
        }));
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className={className}>
          <div className="w-full p-6 flex flex-col gap-[14px] overflow-visible relative bg-white rounded-2xl border border-solid border-[#E2E2E2]">
            <div className="grid grid-cols-2 gap-5 w-full">
              <div className="flex flex-col gap-y-2 col-span-2 sm:col-span-1 justify-start items-start w-full">
                <div className="form-group !mb-0 flex justify-start flex-col gap-2 w-full">
                  <label className="font-medium text-xs text-[#7e7e7e] !mb-0 ">
                    {t("postalCodeCity")}
                  </label>

                  <Select
                    noOptionsMessage={() => null}
                    loadingMessage={() => "..."}
                    // formatOptionLabel={()}
                    options={options}
                    placeholder={t("postalCodeCityPlaceholder")}
                    classNames={
                      "placeholder:!text-[#7e7e7e] !text-[#19295C] !font-bold"
                    }
                    isClearable={true}
                    isLoading={loading}
                    isDisabled={isNearbyActive}
                    inputValue={searchTerm}
                    value={
                      values.zone
                        ? {
                            label: values.zone,
                            value: values.zone,
                          }
                        : null
                    }
                    onInputChange={(inputValue, {action}) => {
                      if (action === "input-change") {
                        if (values.zone && inputValue) {
                          setFieldValue("zone", "", false);
                          setFilter((prev) => ({
                            ...prev,
                            filter: {
                              ...prev.filter,
                              interventionAreas: [],
                            },
                          }));
                        }
                        setSearchTerm(inputValue);
                      }
                    }}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setFieldValue("zone", selectedOption.label);
                        setSearchTerm("");
                        setFilter((prev) => ({
                          ...prev,
                          filter: {
                            ...prev.filter,
                            interventionAreas: [selectedOption.value],
                          },
                        }));
                      } else {
                        setFieldValue("zone", "");
                        setSearchTerm("");
                        setFilter((prev) => ({
                          ...prev,
                          filter: {
                            ...prev.filter,
                            interventionAreas: [],
                          },
                        }));
                      }
                    }}
                    className="w-full"
                    styles={{
                      noOptionsMessage: () => ({
                        display: "none",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        display: "none", // Hide the dropdown indicator
                      }),
                      container: (provided) => ({
                        ...provided, // Override the container style
                        ":focus-within": {borderColor: "#FF6600"}, // Focus border color
                        ":focus": {borderColor: "#FF6600"}, // Focus border color
                        ":hover": {borderColor: "#FF6600"}, // Hover border color
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        borderColor: state.isFocused
                          ? "#e7e7e7"
                          : provided.borderColor, // Focus border color
                        boxShadow: state.isFocused
                          ? "none"
                          : provided.boxShadow, // Focus box shadow
                        ":hover": {
                          borderColor: "#e7e7e7", // Hover border color
                        },
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none", // Hide the indicator separator
                      }),
                      // Custom styles for the input text
                      input: (provided) => ({
                        ...provided,
                        fontSize: "14px", // Reduce text size
                        color: "#19295C", // Input text color
                        ":focus": {
                          borderColor: "#FF6600", // Focus border color
                        },
                        ":hover": {
                          borderColor: "#FF6600", // Focus border color
                        },
                        textTransform: "lowercase",
                        ":first-letter": {
                          textTransform: "uppercase",
                        },
                      }),
                      // Custom styles for the placeholder
                      placeholder: (provided) => ({
                        ...provided,
                        fontSize: "14px", // Reduce placeholder text size
                        color: "#7e7e7e", // Placeholder color
                      }),
                      // Custom styles for the dropdown options
                      option: (provided) => ({
                        ...provided,
                        textTransform: "lowercase",
                        ":first-letter": {
                          textTransform: "uppercase",
                        },
                        padding: "4px 10px",
                        lineHeight: "18px",
                        fontSize: "14px", // Reduce option label text size
                        color: "#5a5a5a", // Option label color
                        ":hover": {
                          background: "#D5E3F3", // Hover background color
                        },
                        ":active": {
                          background: "#D5E3F3", // Hover background color
                        },
                        ":focus": {
                          background: "#D5E3F3", // Hover background color
                        },
                      }),
                      // Custom styles for the single value (selected option)
                      singleValue: (provided) => ({
                        ...provided,
                        fontSize: "14px", // Reduce selected value text size
                        color: "#19295C", // Selected value text color
                        textTransform: "lowercase",
                        ":first-letter": {
                          textTransform: "uppercase",
                        },
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        msOverflowStyle: "none", // Hide scrollbar (IE and Edge)
                        scrollbarWidth: "none", // Hide scrollbar (Firefox)
                      }),
                    }}
                  />
                  {isNearbyActive && (
                    <div className="text-[#1A64B9] text-xs mt-1">
                      {t("nearbyRadiusActive")}
                    </div>
                  )}
                  {errors.zone && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.zone}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col col-span-2 sm:col-span-1 gap-y-3 sm:flex-row justify-start items-start w-full">
                <div className="flex justify-start flex-col gap-1">
                  <div className="font-medium text-xs text-[#7e7e7e] mb-2">
                    {t("providerType")}
                  </div>
                  <label className="block !mb-0 text-[#595959] text-sm cursor-pointer">
                    <Field
                      type="radio"
                      name="type"
                      value={"All"}
                      className="mr-2"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("type", value);
                        setFilter((prev) => ({
                          ...prev,
                          filter: {...prev.filter, type: undefined},
                        }));
                      }}
                    />
                    {t("all")}
                  </label>
                  <label className="block !mb-0 text-[#595959] text-sm cursor-pointer">
                    <Field
                      type="radio"
                      name="type"
                      value={"Pro"}
                      className="mr-2"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("type", value);
                        setFilter((prev) => ({
                          ...prev,
                          filter: {...prev.filter, type: value},
                        }));
                      }}
                    />
                    {t("professional")}
                  </label>
                  <label className="block !mb-0 text-[#595959] text-sm cursor-pointer">
                    <Field
                      type="radio"
                      name="type"
                      value={"Particulier"}
                      className="mr-2"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("type", value);
                        setFilter((prev) => ({
                          ...prev,
                          filter: {...prev.filter, type: value},
                        }));
                      }}
                    />
                    {t("individual")}
                  </label>
                  {errors.type && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.type}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default FilterPrestataire;
